import { useState, useEffect, useRef } from 'react';

const THEMES = [
  {
    id: 'midnight',
    label: 'Midnight Emerald',
    bg: '#0b1628',
    accent: '#34d399',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.6)',
    starColor: '#34d399',
  },
  {
    id: 'clean',
    label: 'Clean White',
    bg: '#ffffff',
    accent: '#0b1628',
    text: '#0b1628',
    subtext: '#666666',
    starColor: '#f59e0b',
  },
  {
    id: 'warm',
    label: 'Warm Gold',
    bg: '#1a1207',
    accent: '#f59e0b',
    text: '#ffffff',
    subtext: 'rgba(255,255,255,0.6)',
    starColor: '#f59e0b',
  },
];

// Instagram Story = 1080x1920, Square = 1080x1080
const FORMATS = [
  { id: 'story', label: 'Story (9:16)', width: 1080, height: 1920 },
  { id: 'square', label: 'Square (1:1)', width: 1080, height: 1080 },
  { id: 'landscape', label: 'Landscape (16:9)', width: 1920, height: 1080 },
];

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? currentLine + ' ' + word : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

function drawCard(canvas, review, theme, format, businessName) {
  const ctx = canvas.getContext('2d');
  const { width, height } = format;
  canvas.width = width;
  canvas.height = height;

  const isStory = format.id === 'story';
  const isSquare = format.id === 'square';
  const padding = isStory ? 80 : 60;
  const contentWidth = width - padding * 2;

  // Background
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, width, height);

  // Subtle gradient overlay
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, 'rgba(255,255,255,0.03)');
  gradient.addColorStop(1, 'rgba(0,0,0,0.05)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // Decorative accent line at top
  ctx.fillStyle = theme.accent;
  ctx.fillRect(0, 0, width, 6);

  // Layout calculations
  const centerY = height / 2;
  let cursorY;

  if (isStory) {
    cursorY = height * 0.2;
  } else if (isSquare) {
    cursorY = height * 0.15;
  } else {
    cursorY = height * 0.15;
  }

  // Large opening quote mark
  ctx.fillStyle = theme.accent;
  ctx.globalAlpha = 0.15;
  ctx.font = `bold ${isStory ? 300 : 200}px Georgia`;
  ctx.textAlign = 'center';
  ctx.fillText('\u201C', width / 2, cursorY + (isStory ? 100 : 60));
  ctx.globalAlpha = 1.0;

  // Stars
  const starSize = isStory ? 48 : 36;
  ctx.font = `${starSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = theme.starColor;
  const stars = '\u2605'.repeat(review.rating) + '\u2606'.repeat(5 - review.rating);
  ctx.fillText(stars, width / 2, cursorY + (isStory ? 160 : 110));
  cursorY += isStory ? 200 : 150;

  // Review text
  const reviewText = review.review_text || '';
  const maxChars = isStory ? 300 : 200;
  let displayText = reviewText;
  if (displayText.length > maxChars) {
    displayText = displayText.substring(0, maxChars).trim() + '...';
  }
  displayText = '\u201C' + displayText + '\u201D';

  const fontSize = isStory ? 42 : (isSquare ? 36 : 34);
  const lineHeight = fontSize * 1.5;
  ctx.font = `italic ${fontSize}px Georgia, serif`;
  ctx.fillStyle = theme.text;
  ctx.textAlign = 'center';

  const lines = wrapText(ctx, displayText, contentWidth);
  const textBlockHeight = lines.length * lineHeight;

  // Center the text block vertically in remaining space
  if (isStory) {
    cursorY = Math.max(cursorY, centerY - textBlockHeight / 2);
  }

  for (const line of lines) {
    ctx.fillText(line, width / 2, cursorY);
    cursorY += lineHeight;
  }

  cursorY += isStory ? 50 : 30;

  // Accent divider
  ctx.fillStyle = theme.accent;
  const dividerWidth = 60;
  ctx.fillRect(width / 2 - dividerWidth / 2, cursorY, dividerWidth, 3);
  cursorY += isStory ? 50 : 35;

  // Reviewer name
  ctx.font = `bold ${isStory ? 28 : 22}px sans-serif`;
  ctx.fillStyle = theme.text;
  ctx.textAlign = 'center';
  ctx.fillText(review.reviewer_name || 'Customer', width / 2, cursorY);
  cursorY += isStory ? 40 : 30;

  // Platform badge
  const platformLabel = {
    google: 'Google Review',
    airbnb: 'Airbnb Review',
    trustpilot: 'Trustpilot Review',
  };
  ctx.font = `${isStory ? 22 : 18}px sans-serif`;
  ctx.fillStyle = theme.subtext;
  ctx.fillText(platformLabel[review.source] || 'Customer Review', width / 2, cursorY);

  // Business name at bottom
  const bottomY = height - (isStory ? 120 : 80);
  ctx.font = `bold ${isStory ? 32 : 24}px sans-serif`;
  ctx.fillStyle = theme.accent;
  ctx.textAlign = 'center';
  ctx.fillText(businessName || '', width / 2, bottomY);

  // Powered by
  ctx.font = `${isStory ? 18 : 14}px sans-serif`;
  ctx.fillStyle = theme.subtext;
  ctx.globalAlpha = 0.5;
  ctx.fillText('stellarreply.com', width / 2, bottomY + (isStory ? 40 : 30));
  ctx.globalAlpha = 1.0;

  // Bottom accent line
  ctx.fillStyle = theme.accent;
  ctx.fillRect(0, height - 6, width, 6);
}

export default function ShareCardModal({ review, businessName, onClose }) {
  const canvasRef = useRef(null);
  const [selectedTheme, setSelectedTheme] = useState(THEMES[0]);
  const [selectedFormat, setSelectedFormat] = useState(FORMATS[0]);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawCard(canvas, review, selectedTheme, selectedFormat, businessName);
    setPreviewUrl(canvas.toDataURL('image/png'));
  }, [review, selectedTheme, selectedFormat, businessName]);

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `review-${review.reviewer_name?.toLowerCase().replace(/\s+/g, '-') || 'card'}-${selectedFormat.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopyImage = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    } catch {
      // Fallback: download instead
      handleDownload();
    }
  };

  // Preview dimensions (scaled down to fit modal)
  const maxPreviewHeight = 400;
  const aspectRatio = selectedFormat.width / selectedFormat.height;
  const previewHeight = Math.min(maxPreviewHeight, selectedFormat.id === 'story' ? 400 : 300);
  const previewWidth = previewHeight * aspectRatio;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '95%' }}>
        <div className="modal-header">
          <h2 className="modal-title">Share Review</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
          Download a branded image to share on social media, WhatsApp, or your website.
        </p>

        {/* Theme selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Theme</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {THEMES.map(theme => (
              <button
                key={theme.id}
                onClick={() => setSelectedTheme(theme)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: selectedTheme.id === theme.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                  background: theme.bg,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <span style={{ position: 'relative', zIndex: 1 }}>{theme.label}</span>
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: theme.accent,
                }} />
              </button>
            ))}
          </div>
        </div>

        {/* Format selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Format</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {FORMATS.map(format => (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: selectedFormat.id === format.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                  background: selectedFormat.id === format.id ? 'var(--green-light, #ecfdf5)' : 'transparent',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: selectedFormat.id === format.id ? 600 : 400,
                  fontFamily: 'inherit',
                  color: 'var(--text)',
                }}
              >
                {format.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: 16,
          background: 'var(--bg)',
          borderRadius: 12,
          marginBottom: 20,
        }}>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Review card preview"
              style={{
                width: previewWidth,
                height: previewHeight,
                borderRadius: 8,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            />
          )}
        </div>

        {/* Hidden canvas for rendering */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-ghost"
            onClick={handleCopyImage}
            title="Copy image to clipboard"
          >
            📋 Copy Image
          </button>
          <button className="btn btn-primary" onClick={handleDownload}>
            ↓ Download Image
          </button>
        </div>
      </div>
    </div>
  );
}
